import {
  applyDictationFill,
  EMPTY_DICTATION_REPORT as SHARED_EMPTY,
  type DictationReportState as SharedReportState,
  type DictationStep as SharedStep,
  planDictation as sharedPlan,
  withDictationTemplate,
} from "@rosc/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type DictationTemplate,
  fillDictationReport,
} from "@/lib/dictation-templates";

// One dictated report (radiology dictation mode). Every Stop hands the final
// transcript to `dictate`; the planning (voice template pick, fill or
// continue) is shared with the Chrome extension in @rosc/contracts
// (dictation-session.ts). Every change can be undone (template switch,
// fill, manual pick).

export type DictationReportState = SharedReportState<DictationTemplate>;
export type DictationStep = SharedStep<DictationTemplate>;
export const EMPTY_DICTATION_REPORT = SHARED_EMPTY as DictationReportState;

export type DictationReportPhase = "idle" | "filling" | "error";

export { applyDictationFill };

/** What one final transcript does (pure; tested on its own). */
export const planDictation = (
  state: DictationReportState,
  transcript: string,
  templates: readonly DictationTemplate[],
): DictationStep => sharedPlan(state, transcript, templates);

const withTemplate = (
  template: DictationTemplate,
  pickedBy: "voice" | "menu",
  dictations: string[],
): DictationStep => withDictationTemplate(template, pickedBy, dictations);

export function useDictationReport(
  templates: readonly DictationTemplate[],
  // A saved report opened again (continue dictating, Copy, switch template).
  initial?: DictationReportState,
) {
  const [state, setState] = useState(initial ?? EMPTY_DICTATION_REPORT);
  const [history, setHistory] = useState<DictationReportState[]>([]);
  const [phase, setPhase] = useState<DictationReportPhase>("idle");
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const failedRef = useRef<{
    before: DictationReportState;
    step: Extract<DictationStep, { kind: "fill" }>;
  } | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  useEffect(() => () => controllerRef.current?.abort(), []);

  const run = useCallback(
    async (before: DictationReportState, step: DictationStep) => {
      setHistory((items) => [...items, before].slice(-20));
      setState(step.state);
      if (step.kind === "state") return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      failedRef.current = null;
      setPhase("filling");
      const started = performance.now();
      try {
        const result = await fillDictationReport(step.input, controller.signal);
        if (controller.signal.aborted) return;
        setDurationMs(Math.round(performance.now() - started));
        setState(applyDictationFill(step.state, result));
        setPhase("idle");
      } catch {
        if (controller.signal.aborted) return;
        failedRef.current = { before, step };
        setPhase("error");
      }
    },
    [],
  );

  const dictate = useCallback(
    (transcript: string) => {
      if (!transcript.trim()) return;
      const before = stateRef.current;
      void run(before, planDictation(before, transcript, templates));
    },
    [run, templates],
  );

  const chooseTemplate = useCallback(
    (template: DictationTemplate) => {
      const before = stateRef.current;
      if (before.template?.id === template.id) return;
      void run(before, withTemplate(template, "menu", before.dictations));
    },
    [run],
  );

  const retry = useCallback(() => {
    const failed = failedRef.current;
    if (!failed) return;
    setHistory((items) => items.slice(0, -1));
    void run(failed.before, failed.step);
  }, [run]);

  const undo = useCallback(() => {
    controllerRef.current?.abort();
    failedRef.current = null;
    setPhase("idle");
    setHistory((items) => {
      const previous = items.at(-1);
      if (previous) setState(previous);
      return items.slice(0, -1);
    });
  }, []);

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    failedRef.current = null;
    setPhase("idle");
    setHistory([]);
    setState(EMPTY_DICTATION_REPORT);
    setDurationMs(null);
  }, []);

  return {
    state,
    phase,
    durationMs,
    canUndo: history.length > 0,
    dictate,
    chooseTemplate,
    retry,
    undo,
    reset,
  };
}
